/*
 * BDEF-CUSTOM: búsqueda predictiva v6.
 * - Mapa de términos en metaobjetos (bdef_search_term, bdef_search_shortcut), servido por
 *   sections/bdef-search-map.liquid y cargado la primera vez que se abre el buscador.
 * - Una petición Storefront GraphQL con alias: capa exact (sintaxis de campo), recuentos de chips
 *   relacionados, capa broad (búsqueda natural con prefix LAST) y colecciones.
 * - Se registra antes que theme.js: Prestige solo define <predictive-search> si no existe.
 * - Fase 2: "Ver todos" e Intro llevan a colecciones o búsquedas con filtros de Search & Discovery.
 * - Medición de interacciones en Microsoft Clarity (eventos, etiquetas de sesión y grabación prioritaria).
 * - v7: búsqueda guiada (fila de refinado Para / Talla / Color / Marca con stock real), autocompletado con
 *   stock y concordancia de género y número, búsquedas recientes, "¿Quisiste decir…?" con corrección tolerante, Intro que espera
 *   al cálculo del destino, cuadros de búsqueda de la página que abren el buscador de la cabecera y aviso
 *   de corrección en destino.
 * Versión anterior guardada en assets/bdef-predictive-search-v5.js.
 */

const CFG = {
  API: '/api/2026-10/graphql.json',
  TOKEN: 'bff0936d5e0922aadc15749ef399fa81',
  MAP_SECTION: 'bdef-search-map',
  MAP_CACHE_KEY: 'bdef-search-map-v1',
  MAP_TTL_MS: 10 * 60 * 1000,
  EXCLUDE_TAG: 'Ocultos OOS',
  EXACT_LIMIT: 18,
  BROAD_LIMIT: 6,
  MAX_CHIPS: 4,
  COLLECTION_LIMIT: 4,
  MIN_CHARS: 2,
  DEBOUNCE_MS: 250,
  TRACK_IDLE_MS: 1200,
  NEW_DAYS: Number(window.bdefConfig?.newBadgeDays) || 60,
  CURRENCY: window.bdefConfig?.currency || 'EUR',
  // Colecciones técnicas que nunca se sugieren en el buscador
  HIDDEN_COLLECTIONS: /^(all|orderlyemails-.*|todos-productos-.*|articulos-de-continuidad|no-rebajas-.*|black-friday|oi\d\d|pv\d\d)$/,
  GENDER_LABEL: { mujer: 'mujer', hombre: 'hombre', kids: 'kids' },
  // Filtros de Search & Discovery usados para construir los enlaces de "Ver todos"
  FACET: { type: 'filter.p.product_type', vendor: 'filter.p.vendor', gender: 'filter.p.m.my_fields.genero', color: 'filter.p.m.my_fields.color', size: 'filter.v.option.talla', avail: 'filter.v.availability' },
  // Búsqueda guiada: una fila de refinado con datos reales de stock, una dimensión cada vez
  REFINE_ORDER: ['gender', 'size', 'color', 'vendor'],
  REFINE_LABEL: { gender: 'Para', size: 'Talla', color: 'Color', vendor: 'Marca' },
  REFINE_MAX: { gender: 3, size: 40, color: 10, vendor: 10 },
  KIDS_TAG: 'Kids',          // los productos infantiles nunca salen en búsquedas de mujer u hombre
  RECENT_KEY: 'bdef-recent-searches',
  RECENT_LIMIT: 6,
  ALL_URL: '/collections/all',
  SUGGEST_LIMIT: 6,          // sugerencias mientras se escribe
  DYM_LIMIT: 3,              // opciones de "¿Quisiste decir…?"
  AUTOFIX_ON_ENTER: true,    // Intro con una errata de corrección única: ir al destino corregido con aviso
  SUBMIT_TIMEOUT_MS: 3000,   // si el cálculo del destino tarda más, Intro va a la búsqueda nativa
  // Ajustes de la tarjeta de producto del tema (los vuelca theme.liquid en window.bdefConfig.card)
  CARD: Object.assign({
    moneyFormat: '{{amount_with_comma_separator}} €',
    moneyWithCurrencyFormat: '{{amount_with_comma_separator}} EUR',
    currencyCode: false,
    showVendor: true,
    showSecondary: true,
    showDiscount: true,
    aspect: 'natural',
    headingFont: 'heading',
    titleLines: 1,
    priceStrategy: 'from_price',
    discountMode: 'percentage',
    titleWrap: false,
    t: {}
  }, window.bdefConfig?.card || {})
};

// ─── CLARITY ────────────────────────────────────────────────────────────────

/** Nombre del evento en Clarity para cada evento interno del buscador. */
const CLARITY_EVENTS = {
  search: 'search',
  search_no_results: 'search_no_results',
  search_chip: 'search_chip',
  select_item: 'search_product_click',
  view_search_results: 'search_view_all',
  search_smart_link: 'search_smart_link',
  search_collection_click: 'search_collection_click',
  search_submit: 'search_submit',
  search_suggestion_click: 'search_suggestion',
  search_fix_undo: 'search_fix_undo',
  search_refine: 'search_refine'
};

/** Nº de resultados agrupado, para filtrar sesiones en Clarity sin una etiqueta por cada número. */
function bucket(n) {
  if (n === null || n === undefined || n === '') return null;
  n = parseInt(n, 10);
  if (!n) return '0';
  if (n <= 5) return '1-5';
  if (n <= 17) return '6-17';
  return '18+';
}

/** Descarta términos con aspecto de dato personal (correo o teléfono) antes de enviarlos a Clarity. */
function safeTerm(t) {
  const s = String(t || '').trim();
  if (!s) return null;
  if (/@/.test(s) || /\b[6789]\d{8}\b/.test(s.replace(/\s+/g, ''))) return '[dato personal omitido]';
  return s;
}

// ─── NORMALIZACIÓN Y ESCAPE ─────────────────────────────────────────────────

/** Minúsculas, sin tildes, sin puntos ni apóstrofos, & aislado, solo [a-z0-9&] y espacios. */
function norm(s) {
  return String(s || '')
    .normalize('NFD').replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[.'´`’]/g, '')
    .replace(/&/g, ' & ')
    .replace(/[^a-z0-9&]+/g, ' ')
    .trim();
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

const quote = v => '"' + String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';

// Lista blanca de campos: es la única vía para construir consultas con sintaxis de campo.
const FIELD = {
  type: v => 'product_type:' + quote(v),
  title: w => { const x = norm(w).replace(/[^a-z0-9]/g, ''); return x ? 'title:' + x : null; },
  vendor: v => 'vendor:' + quote(v),
  tag: v => 'tag:' + quote(v)
};

const anyOf = arr => {
  const a = arr.filter(Boolean);
  if (!a.length) return null;
  return a.length === 1 ? a[0] : '(' + a.join(' OR ') + ')';
};

// ─── MAPA ───────────────────────────────────────────────────────────────────

const EMPTY_MAP = { terms: new Map(), index: new Map(), display: new Map(), prefixes: new Set(), fuzzy: [], vendorUrls: new Map(), maxN: 1, stop: new Set(), shortcuts: [], trending: [], ready: false };

function buildMap(raw) {
  const terms = new Map();
  const index = new Map();
  const stop = new Set();
  let maxN = 1;

  const display = new Map();
  const addAlias = (alias, entry) => {
    const a = norm(alias);
    if (!a) return;
    const prev = index.get(a);
    if (prev && prev !== entry && !entry.auto) console.warn(`[bdef-search] Alias "${a}" repetido en "${prev.h}" y "${entry.h}"`);
    if (prev && entry.auto) return; // las marcas automáticas nunca pisan el mapa
    index.set(a, entry);
    display.set(a, entry.auto ? String(alias).trim() : String(alias).trim().toLowerCase());
    maxN = Math.max(maxN, a.split(' ').length);
  };

  for (const t of raw.terms || []) {
    if (!t || !t.h) continue;
    const e = {
      h: t.h,
      label: t.l || t.h,
      kind: t.k,
      types: t.pt || [],
      titles: t.tw || [],
      mode: t.m || ((t.pt || []).length ? 'tipo' : 'titulo'),
      vendors: t.v || [],
      tags: t.tg || [],
      related: t.r || [],
      cols: t.c || {},
      aliases: t.a || []
    };
    terms.set(e.h, e);
    if (e.kind === 'stopword') { (t.a || []).forEach(a => stop.add(norm(a))); continue; }
    (t.a || []).forEach(a => addAlias(a, e));
  }

  // Marcas del catálogo (shop.vendors): se reconocen sin mantenerlas a mano.
  for (const v of raw.vendors || []) {
    if (!v || !v.n) continue;
    addAlias(v.n, { h: 'vendor:' + norm(v.n), label: v.n, kind: 'marca', auto: true, types: [], titles: [], vendors: [v.n], tags: [], related: [], cols: v.u ? { general: v.u } : {} });
  }

  // Validación de tipos de producto contra el catálogo (solo consola).
  const known = new Set(raw.types || []);
  if (known.size) {
    for (const e of terms.values()) e.types.forEach(tp => { if (!known.has(tp)) console.warn(`[bdef-search] "${e.h}": tipo "${tp}" no existe en la tienda`); });
  }

  const shortcuts = (raw.shortcuts || []).filter(s => s && s.u && s.d)
    .map(s => ({ label: s.l, url: s.u, order: Number(s.o) || 99, triggers: s.d.map(norm).filter(Boolean) }))
    .sort((a, b) => a.order - b.order);

  // Prefijos (4+ letras) de todos los alias y marcas: un atajo nunca se activa por prefijo
  // si lo escrito puede ser el comienzo de un término de producto.
  const prefixes = new Set();
  for (const a of index.keys()) for (let i = 4; i <= a.length; i++) prefixes.add(a.slice(0, i));

  // Alias (5+ letras) candidatos a corregir erratas en términos de producto, con su número de palabras.
  const fuzzy = [];
  for (const [a, e] of index) if (a.length >= 5) fuzzy.push([a, e, a.split(' ').length]);

  const vendorUrls = new Map((raw.vendors || []).filter(v => v && v.n && v.u).map(v => [v.n, v.u]));

  return { terms, index, display, prefixes, fuzzy, maxN, stop, shortcuts, vendorUrls, trending: (raw.trending || []).filter(t => t && t.u), ready: true };
}

let _mapPromise = null;

function loadMap() {
  if (_mapPromise) return _mapPromise;
  _mapPromise = (async () => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CFG.MAP_CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < CFG.MAP_TTL_MS) return buildMap(cached.d);
    } catch (e) { /* sessionStorage no disponible */ }
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}?sections=${CFG.MAP_SECTION}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = (await res.json())[CFG.MAP_SECTION] || '';
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const node = tpl.content.querySelector('#bdef-search-map');
      if (!node) throw new Error('mapa no encontrado');
      const data = JSON.parse(node.textContent);
      try { sessionStorage.setItem(CFG.MAP_CACHE_KEY, JSON.stringify({ t: Date.now(), d: data })); } catch (e) { /* cuota */ }
      return buildMap(data);
    } catch (e) {
      console.warn('[bdef-search] Sin mapa, solo búsqueda natural:', e);
      _mapPromise = null; // reintenta en la siguiente búsqueda
      return EMPTY_MAP;
    }
  })();
  return _mapPromise;
}

// ─── ATAJOS: palabra completa, erratas y prefijo ─────────────────────────────

/** Distancia de edición con transposición (Damerau, variante OSA). */
function editDistance(a, b) {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 2) return 3;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 1; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  }
  return d[m][n];
}

function matchShortcut(text, tokens, map) {
  if (!map.shortcuts.length || !tokens.length) return null;
  const padded = ' ' + text + ' ';

  // 1. Palabra o frase completa: "devolucion", "mi pedido".
  for (const s of map.shortcuts) {
    if (s.triggers.some(t => padded.includes(' ' + t + ' '))) return s;
  }

  // 2. Erratas en palabras completas: "devulucion", "devocion", "whatsap".
  //    Misma primera letra, 5+ letras, y la palabra no es un término del mapa ni una marca.
  for (const tk of tokens) {
    if (tk.length < 5 || /\d/.test(tk) || map.index.has(tk)) continue;
    for (const s of map.shortcuts) {
      for (const t of s.triggers) {
        if (t.includes(' ') || t[0] !== tk[0]) continue;
        const max = t.length >= 8 ? 2 : 1;
        if (editDistance(tk, t) <= max) return s;
      }
    }
  }

  // 3. Prefijo de la última palabra mientras se escribe: "devo", "camb", "whats".
  //    Solo si lo escrito no puede ser el comienzo de un término de producto o una marca.
  const last = tokens[tokens.length - 1];
  if (last.length >= 4 && !/\d/.test(last) && !map.prefixes.has(last)) {
    for (const s of map.shortcuts) {
      if (s.triggers.some(t => !t.includes(' ') && t.startsWith(last))) return s;
    }
  }
  return null;
}

/**
 * Corrige una errata en una palabra completa contra los alias del mapa. Reglas estrictas:
 * 4+ letras, misma primera letra, 1 error (2 si el alias tiene 9+ letras), la palabra no puede
 * ser el comienzo de un alias (el cliente aún está escribiendo) y el mejor candidato debe ser único.
 * Con 4 letras solo se admite la letra que falta ("mujr" → "mujer"), nunca una sustitución.
 */
function fuzzyTerm(tk, map, words = 1) {
  if (tk.length < 4 || /\d/.test(tk) || map.prefixes.has(tk) || map.index.has(tk)) return null;
  let best = 3, hits = new Map();
  for (const [a, e, n] of map.fuzzy) {
    if (n !== words || a[0] !== tk[0] || Math.abs(a.length - tk.length) > 2) continue;
    if (tk.length === 4 && a.length !== 5) continue;
    const max = a.length >= 9 ? 2 : 1;
    const d = editDistance(tk, a);
    if (d > max || d > best) continue;
    if (d < best) { best = d; hits = new Map(); }
    hits.set(e.h, { e, a });
  }
  return hits.size === 1 ? [...hits.values()][0] : null;
}

// ─── SUGERENCIAS ────────────────────────────────────────────────────────────

const KIND_RANK = { tipo: 0, marca: 1, estilo: 2, color: 3, genero: 4, intencion: 5 };

/**
 * "¿Quisiste decir…?": corrección tolerante (hasta 2 errores en palabras de 6+ letras) para las
 * palabras que ni el mapa ni la corrección automática reconocen. Nunca cambia la búsqueda por sí
 * sola en el desplegable: propone. Devuelve [{ text, unique }], la primera opción es la más cercana.
 */
function didYouMean(p, map) {
  const raw = p.text.split(' ');
  // Palabras tal y como las escribió el cliente (con tildes) para las que no se corrigen
  const typed = String(p.raw || '').trim().toLowerCase().split(/\s+/);
  const keep = typed.length === raw.length && typed.every((w, i) => norm(w) === raw[i]) ? typed : raw;
  const lastTyped = raw[raw.length - 1];
  // La última palabra que aún puede completarse ("marró" → marrón) no es una errata: la cubre el autocompletado
  const words = p.rest.filter(w => w.length >= 5 && !/\d/.test(w) && !(w === lastTyped && map.prefixes.has(w)));
  if (!words.length) return [];
  const perWord = words.map(w => {
    const best = new Map();
    for (const [a, e, n] of map.fuzzy) {
      if (n !== 1 || e.kind === 'stopword' || Math.abs(a.length - w.length) > 2) continue;
      if (a[0] !== w[0] && a[1] !== w[1]) continue;
      const max = a.length >= 6 ? 2 : 1;
      const d = editDistance(w, a);
      if (d > max) continue;
      const s = d * 2 + (a[0] !== w[0] ? 1 : 0);   // misma inicial pesa: "frandas" → faldas antes que brancas
      const prev = best.get(e.h);
      if (!prev || s < prev.s || (s === prev.s && a.length < prev.a.length)) best.set(e.h, { a, e, d, s });
    }
    let list = [...best.values()].sort((x, y) => x.s - y.s || (KIND_RANK[x.e.kind] ?? 9) - (KIND_RANK[y.e.kind] ?? 9) || x.a.length - y.a.length);
    if (list.some(c => c.a[0] === w[0])) list = list.filter(c => c.a[0] === w[0]);
    return { w, list, unique: list.length > 0 && (list.length === 1 || list[1].s > list[0].s) };
  }).filter(x => x.list.length);
  if (!perWord.length) return [];

  const build = choice => raw.map((tk, i) => {
    const pw = perWord.find(x => x.w === tk);
    if (!pw) return keep[i];
    const pick = choice[pw.w] || pw.list[0];
    if (pick.e.kind === 'color' && p.noun) return colorForm(pick.e, p.noun);
    return termText(pick.e, pw.w);
  }).join(' ');

  // Opción principal: la mejor corrección de cada palabra. Alternativas solo si están igual de cerca
  // (empate real); una peor no se ofrece ("marro" → marrón, nunca también "marino").
  const out = [{ text: build({}), unique: perWord.every(x => x.unique) }];
  const first = perWord[0];
  first.list.slice(1, CFG.DYM_LIMIT).filter(c => c.s === first.list[0].s)
    .forEach(c => out.push({ text: build({ [first.w]: c }), unique: false }));
  const seen = new Set();
  return out.filter(o => { const k = norm(o.text); if (seen.has(k) || k === p.text) return false; seen.add(k); return true; });
}

/**
 * Forma visible de un término corregido: de las formas del término, la más parecida a lo que se escribió
 * ("azull" → azul, "frañdas" → faldas, "camisetq" → camiseta), con la tilde de la etiqueta cuando es la
 * misma palabra ("marro" → marrón). Nunca una palabra inventada: siempre una forma escrita en el mapa.
 */
function termText(e, typed = '') {
  if (e.kind === 'marca') return e.label;
  const label = String(e.label || '').trim();
  const w = norm(typed);
  let best = null, bestD = Infinity;
  for (const f of e.aliases || []) {
    const n = norm(f);
    if (!n) continue;
    const d = w ? editDistance(w, n) + (n.slice(-1) === w.slice(-1) ? 0 : 0.5) : 0;
    if (d < bestD) { bestD = d; best = f; }
  }
  if (!best) return label.toLowerCase();
  return norm(best) === norm(label) ? label.toLowerCase() : String(best).toLowerCase();
}

/**
 * Concordancia del color con la prenda escrita: "faldas" → negras, "pantalón" → negro, "vaqueros" → azules.
 * Número por la -s final; género por la terminación en -a (salvo excepciones). Las formas salen de los alias
 * del color que comparten raíz con su nombre (negro/negra/negros/negras; nunca las gallegas branco/branca).
 */
const MASC_EN_A = new Set(['pijama', 'pijamas', 'poncho', 'ponchos']);
function nounForm(w) {
  const n = norm(w).split(' ').pop() || '';
  if (!n) return null;
  const plural = /s$/.test(n) && !/(ss|us)$/.test(n);
  const stem = plural ? n.replace(/(es|s)$/, '') : n;
  const fem = !MASC_EN_A.has(n) && (/a$/.test(stem) || /as$/.test(n));
  return { plural, fem };
}
function colorForm(e, noun) {
  const label = String(e.label || '').trim();
  const root = norm(label).slice(0, 3);
  const forms = (e.aliases || []).filter(f => !norm(f).includes(' ') && norm(f).startsWith(root));
  if (!noun || !forms.length) return label.toLowerCase();
  const byNumber = forms.filter(f => /s$/.test(norm(f)) === noun.plural);
  const pool = byNumber.length ? byNumber : forms.filter(f => !/s$/.test(norm(f)));
  const want = noun.fem ? /as?$/ : /os?$/;
  const pick = pool.find(f => want.test(norm(f))) || pool[0] || forms[0];
  return norm(pick) === norm(label) ? label.toLowerCase() : String(pick).toLowerCase();
}

/** Texto con las correcciones automáticas aplicadas ("zapatllas" → "zapatillas"), para ofrecerlo en /search. */
function correctedText(p, map) {
  if (!p.corrections.length) return null;
  // Las palabras no corregidas se dejan como las escribió el cliente (con tildes) cuando se pueden alinear
  const tokens = p.text.split(' ');
  const typed = String(p.raw || '').trim().toLowerCase().split(/\s+/);
  const out = typed.length === tokens.length && typed.every((w, i) => norm(w) === tokens[i]) ? typed.slice() : tokens.slice();
  let from = 0;
  p.corrections.forEach(c => {
    const parts = c.from.split(' ');
    for (let i = from; i + parts.length <= tokens.length; i++) {
      if (parts.every((x, k) => tokens[i + k] === x)) {
        out.splice(i, parts.length, c.show, ...Array(parts.length - 1).fill(null));
        from = i + parts.length;
        break;
      }
    }
  });
  return out.filter(x => x !== null).join(' ');
}

/** Firma de lo que el mapa entiende de una búsqueda: términos reconocidos y palabras sueltas. */
function meaning(p) {
  return [...p.tipo, ...p.estilo, ...p.color, ...p.marca].map(t => t.h)
    .concat(p.genero ? ['g:' + p.genero.h] : [], p.intencion ? ['i:' + p.intencion.h] : [], p.rest)
    .sort().join('|');
}

/** Sugerencias mientras se escribe: términos del mapa que empiezan por la última palabra y sugerencias de Shopify. */
function typeahead(p, map, shopifyQueries = [], baseFacets = null) {
  const rawWords = String(p.raw || '').trim().split(/\s+/);
  const tokens = p.text ? p.text.split(' ') : [];
  const last = tokens[tokens.length - 1] || '';
  const head = rawWords.slice(0, -1).join(' ');
  const out = [], seen = new Set();
  const push = (s, count = null) => {
    const text = String(s).replace(/\s+/g, ' ').trim();
    const k = norm(text);
    if (!k || k === p.text || seen.has(k)) return;
    seen.add(k); out.push({ text, count });
  };
  // Stock de la búsqueda sin la palabra a medias ("camiseta" para "camiseta ma"): colores y marcas
  // solo si tienen producto, ordenados por cuántos hay
  const stockOf = e => {
    if (!baseFacets) return null;
    const id = e.kind === 'color' ? CFG.FACET.color : e.kind === 'marca' ? CFG.FACET.vendor : null;
    if (!id) return null;
    const f = baseFacets.find(x => x.id === id);
    if (!f) return null;
    const names = e.kind === 'marca' ? e.vendors : [e.label, ...(e.aliases || [])];
    const want = new Set(names.map(norm));
    return f.values.filter(v => want.has(norm(v.label))).reduce((n, v) => n + (v.count || 0), 0);
  };
  if (last.length >= 2 && !/^\d+$/.test(last)) {
    const hits = new Map();
    const current = map.index.get(last);
    for (const [a, e] of map.index) {
      if (!a.startsWith(last) || a === last || e.kind === 'stopword' || e === current) continue;
      // "falda ne" no propone otra prenda (neceseres), salvo que juntas sean un término ("pantalón vaq" → pantalón vaquero)
      if (e.kind === 'tipo' && p.tipo.length && !p.tipo.includes(current) && !map.index.has(norm(`${head} ${a}`).split(' ').slice(-2).join(' '))) continue;
      if (!norm(e.label).startsWith(last)) continue;   // solo si el nombre visible también empieza así ("mu" no propone Zuecos por "mule")
      if (a.split(' ').length > 3) continue;
      const prev = hits.get(e.h);
      if (!prev || a.length < prev.a.length) hits.set(e.h, { a, e });
    }
    [...hits.values()]
      .map(h => ({ ...h, stock: stockOf(h.e) }))
      .filter(h => h.stock === null || h.stock > 0)
      .sort((x, y) => (KIND_RANK[x.e.kind] ?? 9) - (KIND_RANK[y.e.kind] ?? 9) || (y.stock ?? 0) - (x.stock ?? 0) || x.a.length - y.a.length)
      .slice(0, 4)
      .forEach(h => push(`${head} ${h.e.kind === 'marca' ? h.e.label : h.e.kind === 'color' ? colorForm(h.e, p.noun) : String(h.e.label).toLowerCase()}`, h.stock));
  }
  // Mujer / hombre ya no se sugiere como texto: lo ofrece la fila de refinado con el stock real
  shopifyQueries
    .map(q => q.text)
    .filter(q => q && q.split(/\s+/).length <= 3)
    .forEach(push);
  // Fuera las que el mapa interpreta igual que lo ya escrito ("falda" → "faldas" no aporta nada)
  // y las que repiten significado entre sí ("zapatillas mujer" / "zapatilla mujer")
  const used = new Set([meaning(p)]);
  return out.filter(o => {
    const m = meaning(parse(o.text, map));
    if (used.has(m)) return false;
    used.add(m);
    return true;
  }).slice(0, CFG.SUGGEST_LIMIT);
}

// ─── PARSER ─────────────────────────────────────────────────────────────────

function parse(raw, map) {
  const text = norm(raw);
  const tokens = text ? text.split(' ') : [];
  const p = { raw, text, tipo: [], estilo: [], color: [], marca: [], genero: null, intencion: null, rest: [], code: null, broadTokens: [], shortcut: null, corrections: [], noun: null };

  p.shortcut = matchShortcut(text, tokens, map);

  for (let i = 0; i < tokens.length;) {
    let hit = null, len = 0;
    for (let k = Math.min(map.maxN, tokens.length - i); k >= 1; k--) {
      const phrase = tokens.slice(i, i + k).join(' ');
      if (map.index.has(phrase)) { hit = map.index.get(phrase); len = k; break; }
    }
    if (hit) {
      const consumed = tokens.slice(i, i + len);
      i += len;
      if (hit.kind === 'genero') { p.genero = hit; continue; }
      if (hit.kind === 'intencion') { p.intencion = hit; continue; }
      if (p[hit.kind] && !p[hit.kind].includes(hit)) p[hit.kind].push(hit);
      if (hit.kind === 'tipo' && !p.noun) p.noun = nounForm(consumed.join(' '));
      p.broadTokens.push(...consumed);
      continue;
    }
    // Errata: primero frases de varias palabras ("salsa jeens"), luego la palabra suelta.
    let fz = null, fzLen = 0;
    if (!p.shortcut) {
      for (let k = Math.min(map.maxN, tokens.length - i); k >= 1 && !fz; k--) {
        if (k > 1 && tokens.slice(i, i + k).some(t => map.stop.has(t))) continue;
        fz = fuzzyTerm(tokens.slice(i, i + k).join(' '), map, k);
        if (fz) fzLen = k;
      }
    }
    if (fz) {
      i += fzLen;
      const tk = tokens.slice(i - fzLen, i).join(' ');
      const e = fz.e;
      p.corrections.push({ from: tk, to: fz.a, label: e.label, kind: e.kind, e, show: termText(e, tk) });
      if (e.kind === 'tipo' && !p.noun) p.noun = nounForm(termText(e, tk));
      if (e.kind === 'genero') p.genero = e;
      else if (e.kind === 'intencion') p.intencion = e;
      else if (p[e.kind] && !p[e.kind].includes(e)) p[e.kind].push(e);
      if (e.kind !== 'genero' && e.kind !== 'intencion') p.broadTokens.push(fz.a);
      continue;
    }
    const tk = tokens[i++];
    if (tk === '&' || map.stop.has(tk)) continue;
    if (/^\d{1,3}$/.test(tk)) continue;                         // tallas: "42", "xl" no llega aquí
    if (/\d/.test(tk) && tk.length >= 5) { p.code = tk; p.broadTokens.push(tk); continue; } // SKU o referencia
    p.broadTokens.push(tk);
    if (tk.length >= 2) p.rest.push(tk);
  }
  // Colores corregidos en concordancia con la prenda ("faldas negrs" → faldas negras)
  if (p.noun) p.corrections.forEach(c => { if (c.kind === 'color') c.show = colorForm(c.e, p.noun); });
  return p;
}

// ─── COMPILADOR ─────────────────────────────────────────────────────────────

function termClause(t) {
  const T = anyOf(t.types.map(FIELD.type));
  const W = anyOf(t.titles.map(FIELD.title));
  switch (t.mode) {
    case 'tipo': return T || W;
    case 'titulo': return W || T;
    case 'tipo_y_titulo': return T && W ? `(${T} AND ${W})` : (T || W);
    case 'tipo_o_titulo': return anyOf(t.types.map(FIELD.type).concat(t.titles.map(FIELD.title)));
    default: return T || W;
  }
}

function filterClauses(p) {
  const out = [];
  if (p.genero && p.genero.tags.length) out.push(anyOf(p.genero.tags.map(FIELD.tag)));
  if (p.genero && (p.genero.h === 'mujer' || p.genero.h === 'hombre')) out.push('NOT ' + FIELD.tag(CFG.KIDS_TAG));
  if (p.intencion && p.intencion.tags.length) out.push(anyOf(p.intencion.tags.map(FIELD.tag)));
  out.push('NOT ' + FIELD.tag(CFG.EXCLUDE_TAG));
  return out;
}

/** Consulta de la capa exact para un término principal dado (o ninguno). */
function compile(p, primary) {
  const core = [];
  if (primary) core.push(termClause(primary));
  p.estilo.forEach(t => core.push(termClause(t)));
  p.color.forEach(t => core.push(termClause(t)));
  if (p.marca.length) core.push(anyOf(p.marca.flatMap(t => t.vendors).map(FIELD.vendor)));
  p.rest.forEach(w => core.push(FIELD.title(w)));
  const clean = core.filter(Boolean);
  if (!clean.length && !p.intencion) return null;
  return clean.concat(filterClauses(p)).join(' AND ');
}

function planSearch(p, map) {
  const chips = [];
  const primary = p.tipo[0] || null;
  if (primary) {
    const seen = new Set();
    const add = t => { if (t && !seen.has(t.h) && chips.length < CFG.MAX_CHIPS) { seen.add(t.h); chips.push({ term: t, query: compile(p, t) }); } };
    add(primary);
    p.tipo.slice(1).forEach(add);
    primary.related.forEach(h => add(map.terms.get(h)));
  }
  const exact = primary ? chips[0].query : (p.code ? null : compile(p, null));
  const broadText = p.broadTokens.join(' ').trim();

  // Novedades por fecha de creación (misma ventana que la insignia): la búsqueda de Shopify
  // ignora los filtros de fecha, así que este modo usa la consulta products con su propio filtro.
  if (p.intencion && !p.intencion.tags.length) {
    return { novelty: true, primary, chips: primary ? [{ term: primary, query: null }] : [], exact: null, broad: null, colq: null };
  }
  return {
    chips,
    exact,
    broad: broadText.length >= CFG.MIN_CHARS ? broadText : null,
    colq: broadText.length >= 3 ? broadText : null
  };
}

// ─── NOVEDADES POR FECHA ─────────────────────────────────────────────────────

/** Parte que filtra el servidor: fecha, stock, exclusión, género, marca y tipo cuando el modo lo permite. */
function noveltyQuery(p, primary) {
  const since = new Date(Date.now() - CFG.NEW_DAYS * 86400000).toISOString().slice(0, 10);
  const parts = [`created_at:>'${since}'`, 'available_for_sale:true', 'NOT ' + FIELD.tag(CFG.EXCLUDE_TAG)];
  if (p.genero && p.genero.tags.length) parts.push(anyOf(p.genero.tags.map(FIELD.tag)));
  if (p.genero && (p.genero.h === 'mujer' || p.genero.h === 'hombre')) parts.push('NOT ' + FIELD.tag(CFG.KIDS_TAG));
  if (p.marca.length) parts.push(anyOf(p.marca.flatMap(t => t.vendors).map(FIELD.vendor)));
  if (primary && primary.types.length && (primary.mode === 'tipo' || primary.mode === 'tipo_y_titulo')) parts.push(anyOf(primary.types.map(FIELD.type)));
  return parts.join(' AND ');
}

/** Parte que se filtra en el navegador: el filtro title: no funciona en la consulta products. */
function titleWords(n) {
  return new Set(norm(n.title).split(' '));
}
function matchTermLocal(n, words, t) {
  const byType = t.types.includes(n.productType);
  const byTitle = t.titles.some(w => words.has(norm(w)));
  switch (t.mode) {
    case 'tipo': return t.types.length ? byType : byTitle;
    case 'titulo': return t.titles.length ? byTitle : byType;
    case 'tipo_y_titulo': return (t.types.length ? byType : true) && (t.titles.length ? byTitle : true);
    case 'tipo_o_titulo': return byType || byTitle;
    default: return byType || byTitle;
  }
}
function matchNoveltyLocal(n, p, primary) {
  const words = titleWords(n);
  if (primary && !matchTermLocal(n, words, primary)) return false;
  if (!p.estilo.every(t => matchTermLocal(n, words, t))) return false;
  if (!p.color.every(t => matchTermLocal(n, words, t))) return false;
  return p.rest.every(w => [...words].some(x => x === w || x.startsWith(w)));
}

// ─── GRAPHQL ────────────────────────────────────────────────────────────────

const PRODUCT_FIELDS = 'id handle title vendor productType createdAt trackingParameters featuredImage { url width height altText } images(first: 2) { nodes { url width height altText } } priceRange { minVariantPrice { amount currencyCode } maxVariantPrice { amount } } compareAtPriceRange { minVariantPrice { amount } maxVariantPrice { amount } } badges: metafield(namespace: "custom", key: "badges") { value } cardImage: metafield(namespace: "custom", key: "card_image") { reference { ... on MediaImage { image { url width height altText } } } } cardHover: metafield(namespace: "custom", key: "card_hover_image") { reference { ... on MediaImage { image { url width height altText } } } }';

function buildRequest(plan, filters = []) {
  const defs = [], body = [], vars = {};
  if (plan.exact) {
    defs.push('$qe: String!', '$pf: [ProductFilter!]'); vars.qe = plan.exact; vars.pf = filters;
    body.push(`exact: search(query: $qe, first: ${CFG.EXACT_LIMIT}, types: PRODUCT, unavailableProducts: HIDE, productFilters: $pf) { totalCount productFilters { id values { label count input } } nodes { ... on Product { ${PRODUCT_FIELDS} } } }`);
  }
  plan.chips.forEach((c, i) => {
    if (i === 0 || !c.query || filters.length) return;
    defs.push(`$q${i}: String!`); vars['q' + i] = c.query;
    body.push(`c${i}: search(query: $q${i}, first: 1, types: PRODUCT, unavailableProducts: HIDE) { totalCount }`);
  });
  if (plan.broad && !filters.length) {
    defs.push('$qb: String!'); vars.qb = plan.broad;
    body.push(`broad: search(query: $qb, first: ${CFG.BROAD_LIMIT + 6}, types: PRODUCT, prefix: LAST, unavailableProducts: HIDE) { nodes { ... on Product { tags ${PRODUCT_FIELDS} } } }`);
  }
  if (plan.colq && !filters.length) {
    defs.push('$qc: String!'); vars.qc = plan.colq;
    body.push(`cols: predictiveSearch(query: $qc, limit: ${CFG.COLLECTION_LIMIT + 4}, types: [COLLECTION, QUERY]) { collections { handle title trackingParameters } queries { text } }`);
  }
  if (!body.length) return null;
  return { query: `query BdefSearch(${defs.join(', ')}) { ${body.join(' ')} }`, variables: vars };
}

async function gql(req, signal) {
  const res = await fetch(CFG.API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': CFG.TOKEN },
    body: JSON.stringify(req),
    signal
  });
  if (!res.ok) throw new Error('Storefront HTTP ' + res.status);
  const json = await res.json();
  if (json.errors) console.warn('[bdef-search] GraphQL:', json.errors);
  return json.data || {};
}

// ─── BÚSQUEDA GUIADA ────────────────────────────────────────────────────────

/** Filtros de la Storefront API para el refinado elegido. La talla va siempre con disponibilidad:
 *  juntas filtran por variante ("M disponible"); la talla sola devolvería prendas con la M agotada. */
function refineFilters(refine) {
  const out = [];
  ['gender', 'color', 'vendor', 'size'].forEach(d => { if (refine?.[d]) out.push(JSON.parse(refine[d].input)); });
  if (refine?.size) out.push({ available: true });
  return out;
}
function refineKey(refine) {
  return ['gender', 'size', 'color', 'vendor'].map(d => refine?.[d]?.label || '').join('|');
}
const SIZE_ORDER = ['xxs', 'xs', 'xs/s', 's', 's/m', 'm', 'm/l', 'l', 'l/xl', 'xl', 'xxl', '2xl', 'xxxl', '3xl', '4xl'];
function sizeRank(label) {
  const l = String(label).toLowerCase().trim();
  const i = SIZE_ORDER.indexOf(l);
  if (i >= 0) return i;
  const n = parseFloat(l.replace(',', '.'));
  return Number.isFinite(n) ? 100 + n : 1000;
}

/** Recientes del cliente (solo en su navegador). */
function readRecent() {
  try { return JSON.parse(localStorage.getItem(CFG.RECENT_KEY) || '[]').filter(Boolean).slice(0, CFG.RECENT_LIMIT); } catch (e) { return []; }
}
function saveRecent(term) {
  const t = String(term || '').trim();
  if (t.length < CFG.MIN_CHARS || /@/.test(t)) return;
  try {
    const list = [t, ...readRecent().filter(x => norm(x) !== norm(t))].slice(0, CFG.RECENT_LIMIT);
    localStorage.setItem(CFG.RECENT_KEY, JSON.stringify(list));
  } catch (e) { /* almacenamiento no disponible */ }
}

// ─── ESTILOS ────────────────────────────────────────────────────────────────

let _styles = false;
function injectStyles() {
  if (_styles) return;
  _styles = true;
  const css = `
.bdef-ps{width:100%;max-height:70vh;overflow-y:auto;-webkit-overflow-scrolling:touch;padding-bottom:24px;background:#FBFBFB}
@media(min-width:750px){.bdef-ps{max-height:none;overflow:visible}}
.bdef-ps :focus-visible{outline:2px solid #2c2c2c;outline-offset:2px}
.bdef-ps__banner{display:flex;align-items:center;gap:12px;margin:10px;padding:12px 14px;background:#fff;border:1px solid #e1e1e1;border-radius:6px;color:#333;text-decoration:none;font-size:13px;font-weight:600}
.bdef-ps__banner small{display:block;font-size:11px;font-weight:400;color:#666}
.bdef-ps__banner svg{margin-left:auto;flex:none;color:#999}
.bdef-ps__chips{display:flex;gap:8px;overflow-x:auto;padding:12px 10px 4px;scrollbar-width:none}
.bdef-ps__chips::-webkit-scrollbar{display:none}
.bdef-ps__chip{flex:none;display:inline-flex;align-items:baseline;gap:6px;padding:8px 14px;border:1px solid #d6d6d6;border-radius:4px;background:#fff;color:#333;font:inherit;font-size:12px;font-weight:600;cursor:pointer}
.bdef-ps__chip span{font-weight:400;color:#777}
.bdef-ps__chip[aria-pressed="true"]{background:#2c2c2c;border-color:#2c2c2c;color:#fff}
.bdef-ps__chip[aria-pressed="true"] span{color:#ccc}
.bdef-ps__grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:12px;row-gap:24px;padding:16px 12px;margin:0;align-items:start}
@media(min-width:700px){.bdef-ps__grid{grid-template-columns:repeat(3,minmax(0,1fr));column-gap:16px;row-gap:32px;padding:20px}}
@media(min-width:1000px){.bdef-ps__grid{grid-template-columns:repeat(5,minmax(0,1fr));column-gap:24px}}
.bdef-ps__grid[aria-busy="true"]{opacity:.45}
.bdef-ps__grid product-card{min-width:0}
.bdef-ps__grid .product-card__info{text-align:left}
.bdef-ps__more{display:flex;flex-wrap:wrap;gap:10px 18px;justify-content:center;padding:15px;border-top:1px solid #eee}
.bdef-ps__more a{font-size:12px;font-weight:600;color:inherit;text-decoration:underline;text-underline-offset:3px;text-transform:uppercase}
.bdef-ps__section{padding:14px 10px 0;font-size:12px;font-weight:600;color:#555}
.bdef-ps__cols{display:flex;flex-wrap:wrap;gap:8px;padding:8px 10px 0}
.bdef-ps__cols a{padding:7px 12px;border:1px solid #e1e1e1;border-radius:4px;background:#fff;color:#333;text-decoration:none;font-size:12px}
.bdef-ps__empty{padding:22px 15px 8px;text-align:center;color:#555;font-size:13px;line-height:1.5}
.bdef-ps__trend{display:flex;flex-wrap:wrap;gap:8px;justify-content:center;padding:12px 15px 0}
.bdef-ps__trend a{padding:8px 12px;border:1px solid #e1e1e1;border-radius:4px;background:#fff;color:#333;text-decoration:none;font-size:12px;font-weight:600}
.bdef-ps__dym{padding:14px 12px 0;font-size:13px;color:#333}
.bdef-ps__dym button{font:inherit;font-weight:600;color:inherit;background:none;border:0;padding:0;text-decoration:underline;text-underline-offset:3px;cursor:pointer}
.bdef-ps__sugg{display:flex;flex-wrap:wrap;gap:6px 8px;padding:12px 10px 0}
.bdef-ps__sugg button{display:inline-flex;align-items:center;gap:6px;padding:7px 12px;border:1px solid #e1e1e1;border-radius:4px;background:#fff;color:#333;font:inherit;font-size:12px;cursor:pointer}
.bdef-ps__sugg svg{flex:none;color:#999}
.bdef-ps__sugg button span{color:#999}
.bdef-ps__refine{display:flex;align-items:center;gap:8px;overflow-x:auto;padding:12px 10px 4px;scrollbar-width:none;white-space:nowrap}
.bdef-ps__refine::-webkit-scrollbar{display:none}
.bdef-ps__refine .bdef-ps__chip{min-height:40px;align-items:center}
.bdef-ps__refine-label{flex:none;padding:0 2px 0 4px;font-size:12px;font-weight:600;color:#555}
.bdef-ps__refine-more{flex:none;min-height:40px;padding:0 6px;background:none;border:0;color:#333;font:inherit;font-size:12px;text-decoration:underline;text-underline-offset:3px;cursor:pointer}
.bdef-ps--zero .bdef-ps__section{text-align:left!important}
.bdef-ps--zero .bdef-ps__trend{justify-content:flex-start;padding:8px 10px 0}
.bdef-ps__section--row{display:flex;justify-content:space-between;align-items:baseline;padding-right:12px}
.bdef-ps__section--row button{background:none;border:0;padding:0;color:#777;font:inherit;font-size:12px;font-weight:400;text-decoration:underline;text-underline-offset:3px;cursor:pointer}
@media(max-width:749px){.bdef-ps__refine{position:sticky;top:0;z-index:2;background:#FBFBFB}.bdef-ps--refining .bdef-ps__chips{display:none}}
@media(hover:hover){.bdef-ps__banner:hover,.bdef-ps__cols a:hover,.bdef-ps__trend a:hover,.bdef-ps__sugg button:hover{background:#f0f0f0}}
`;
  const style = document.createElement('style');
  style.id = 'bdef-predictive-search-styles';
  style.textContent = css;
  document.head.appendChild(style);
}

const CHEVRON_SEARCH = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><line x1="20" y1="20" x2="16.5" y2="16.5"></line></svg>';
const CHEVRON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>';

// ─── COMPONENTE ─────────────────────────────────────────────────────────────

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.abortController = null;
    this.state = null;
    this.trackTimer = null;
    this.lastTracked = '';
    this.refine = {};          // refinado elegido en la fila guiada: { gender, size, color, vendor } → { label, input }
    this.refineFor = '';       // búsqueda (normalizada) a la que pertenece ese refinado
    this.skipped = new Set();  // dimensiones saltadas con "Más filtros"
    this.attachShadow({ mode: 'open' }).innerHTML = '<slot name="results"></slot>';
  }

  connectedCallback() {
    this.form = document.querySelector(`[aria-owns="${this.id}"]`) || this.closest('form');
    if (!this.form) return;
    this.input = this.form.elements.q;
    this.slotEl = this.querySelector('[slot="results"]');
    if (!this.input || !this.slotEl) return;

    const warm = () => loadMap();
    this.input.addEventListener('focus', warm, { once: true });
    // Caja vacía al abrir: búsquedas recientes del cliente y lo más buscado
    this.input.addEventListener('focus', () => { if (!this.input.value.trim()) this.renderZero(); });

    this.input.addEventListener('input', this.debounce(() => this.onInput(), CFG.DEBOUNCE_MS));

    this.form.addEventListener('submit', e => {
      e.preventDefault();
      const v = this.input.value.trim();
      if (v) this.go(v, 'header');
    });

    this.form.addEventListener('reset', () => { this.abortController?.abort(); this.clear(); });

    this.slotEl.addEventListener('click', e => this.onClick(e));
  }

  clear() {
    this.state = null;
    this.slotEl.innerHTML = '';
  }

  debounce(fn, wait) {
    let t;
    return () => { clearTimeout(t); t = setTimeout(fn, wait); };
  }

  // ── Flujo principal ───────────────────────────────────────────────────────

  async onInput() {
    const term = this.input.value.trim();
    if (term.length < CFG.MIN_CHARS) {
      this.abortController?.abort();
      if (!term) this.renderZero(); else this.clear();
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const map = await loadMap();
      this._trending = map.trending;
      this._vendorUrls = map.vendorUrls;
      this._map = map;
      const p = parse(term, map);
      // Otra búsqueda: el refinado anterior deja de aplicarse
      if (p.text !== this.refineFor) { this.refine = {}; this.skipped = new Set(); this.refineFor = p.text; }
      const refine = this.refine;
      const key = `${p.text}#${refineKey(refine)}`;
      let result = this.cache.get(key);
      if (!result) {
        result = await this.search(p, map, signal, refine);
        if (signal.aborted) return;
        this.cache.set(key, result);
      }
      if (this.input.value.trim() !== term) return;
      this.render(term, p, result);
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[bdef-search]', e);
        this.renderError(term);
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  async search(p, map, signal, refine = {}) {
    let plan = planSearch(p, map);
    if (plan.novelty) return this.searchNovelty(p, plan, signal);
    // Mujer u hombre elegido en la fila guiada: fuera lo infantil aunque su metacampo de género diga otra cosa
    const refinedGender = refine.gender ? norm(refine.gender.label) : '';
    if (plan.exact && (refinedGender === 'mujer' || refinedGender === 'hombre')) plan = { ...plan, exact: `${plan.exact} AND NOT ${FIELD.tag(CFG.KIDS_TAG)}` };
    const filters = plan.exact ? refineFilters(refine) : [];
    const req = buildRequest(plan, filters);
    const data = req ? await gql(req, signal) : {};

    const exactNodes = (data.exact?.nodes || []).filter(n => n.featuredImage);
    const exactTotal = data.exact?.totalCount || 0;

    const chips = plan.chips.map((c, i) => ({
      term: c.term,
      query: c.query,
      count: i === 0 ? exactTotal : (data['c' + i]?.totalCount || 0),
      nodes: i === 0 ? exactNodes : null
    })).filter((c, i) => i === 0 || c.count > 0);

    const genderTags = p.genero ? p.genero.tags.map(t => t.toLowerCase()) : null;
    const seen = new Set(exactNodes.map(n => n.handle));
    const broadNodes = (data.broad?.nodes || []).filter(n => {
      if (!n.featuredImage || seen.has(n.handle)) return false;
      const tags = (n.tags || []).map(t => t.toLowerCase());
      if (tags.includes(CFG.EXCLUDE_TAG.toLowerCase())) return false;
      if (genderTags && !tags.some(t => genderTags.includes(t))) return false;
      return true;
    }).slice(0, CFG.BROAD_LIMIT);

    const collections = (data.cols?.collections || []).filter(c => !CFG.HIDDEN_COLLECTIONS.test(c.handle)).slice(0, CFG.COLLECTION_LIMIT);

    const queries = data.cols?.queries || [];
    return { plan, chips, exactNodes, exactTotal, broadNodes, collections, queries, refine: { ...refine }, filters, sizeCounts: null, facets: data.exact?.productFilters || [] };
  }

  async searchNovelty(p, plan, signal) {
    const q = noveltyQuery(p, plan.primary);
    const pr = plan.primary;
    // Solo se descarga en volumen cuando hay que filtrar por título en el navegador.
    const needsLocal = !!(p.estilo.length || p.color.length || p.rest.length || (pr && (pr.mode === 'titulo' || pr.mode === 'tipo_o_titulo' || (pr.mode === 'tipo_y_titulo' && pr.titles.length))));
    const size = needsLocal ? 250 : 100;
    const Q = `query BdefNew($q: String!, $a: String) { products(first: ${size}, after: $a, query: $q, sortKey: CREATED_AT, reverse: true) { pageInfo { hasNextPage endCursor } nodes { ${PRODUCT_FIELDS} } } }`;
    let after = null, all = [], capped = false;
    for (let page = 0; page < (needsLocal ? 2 : 1); page++) {
      const data = await gql({ query: Q, variables: { q, a: after } }, signal);
      const conn = data.products;
      if (!conn) break;
      all = all.concat(conn.nodes);
      capped = conn.pageInfo.hasNextPage;
      if (!capped) break;
      after = conn.pageInfo.endCursor;
    }
    let nodes = all.filter(n => n.featuredImage && matchNoveltyLocal(n, p, pr));
    // Si una palabra no reconocida deja la búsqueda vacía, se ignora y se avisa de que es aproximada.
    let approx = false;
    if (!nodes.length && p.rest.length) {
      const loose = { ...p, rest: [] };
      nodes = all.filter(n => n.featuredImage && matchNoveltyLocal(n, loose, pr));
      approx = nodes.length > 0;
    }
    const total = capped ? `${nodes.length}+` : nodes.length;
    const chips = pr ? [{ term: pr, query: null, count: total, nodes: nodes.slice(0, CFG.EXACT_LIMIT) }] : [];
    let facets = [];
    const fq = compile(p, pr);
    if (fq && (p.color.length || p.genero)) {
      try {
        const fd = await gql({ query: 'query BdefFacets($q: String!) { search(query: $q, first: 1, types: PRODUCT) { productFilters { id values { label input } } } }', variables: { q: fq } }, signal);
        facets = fd.search?.productFilters || [];
      } catch (e) { /* sin facetas, los enlaces van sin color */ }
    }
    return { plan, chips, exactNodes: nodes.slice(0, CFG.EXACT_LIMIT), exactTotal: total, broadNodes: [], collections: [], approx, facets };
  }

  // ── Intro: destino calculado antes de navegar ──────────────────────────────

  /** Calcula a dónde lleva Intro para un texto. Misma lógica que el desplegable, sin depender de que haya terminado. */
  async resolveSubmit(v, allowFix = CFG.AUTOFIX_ON_ENTER) {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const nativeUrl = `${root}/search?q=${encodeURIComponent(v)}&type=product&options%5Bprefix%5D=last`;
    const map = await loadMap();
    if (!map.ready) return { url: nativeUrl, total: null, destination: 'search' };
    const p = parse(v, map);

    // Atajo de servicio sin términos de producto: directo al atajo
    if (p.shortcut && !p.tipo.length && !p.marca.length && !p.estilo.length && !p.color.length && !p.intencion) {
      return { url: p.shortcut.url, total: null, destination: 'shortcut' };
    }

    // El refinado elegido en la fila guiada viaja a Intro si es de esta misma búsqueda
    const refine = allowFix && this.refineFor === p.text ? this.refine : {};
    const key = `${p.text}#${refineKey(refine)}`;
    let r = this.cache.get(key);
    if (!r) { r = await this.search(p, map, undefined, refine); this.cache.set(key, r); }
    const hasExact = r.exactNodes.length > 0;

    // Errata que solo la corrección tolerante resuelve: destino de la búsqueda corregida, con aviso
    if (!hasExact && allowFix && p.rest.length) {
      const dym = didYouMean(p, map);
      if (dym.length && dym[0].unique) {
        const fixed = await this.resolveSubmit(dym[0].text, false);
        if (fixed.destination !== 'search') {
          const sep = fixed.url.includes('?') ? '&' : '?';
          return { ...fixed, url: `${fixed.url}${sep}bdef_as=${encodeURIComponent(dym[0].text)}&bdef_q=${encodeURIComponent(v)}`, fix: dym[0].text };
        }
      }
    }

    const va = this.viewAllFor(p, r.chips[0]?.term || null, r.facets || [], !(r.plan.exact && !hasExact), r.refine);
    const total = hasExact ? r.exactTotal : r.broadNodes.length;
    const url = va.enter || (va.length === 1 ? va[0].url : null);
    if (!url) return { url: nativeUrl, total, destination: 'search' };
    return { url, total, destination: url.includes('/search?') ? 'search' : 'collection' };
  }

  /** Navega al destino calculado; si tarda demasiado o falla, a la búsqueda nativa. */
  async go(v, source) {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const fallback = { url: `${root}/search?q=${encodeURIComponent(v)}&type=product&options%5Bprefix%5D=last`, total: null, destination: 'search' };
    let res = fallback;
    try {
      res = await Promise.race([
        this.resolveSubmit(v),
        new Promise(resolve => setTimeout(() => resolve(fallback), CFG.SUBMIT_TIMEOUT_MS))
      ]);
    } catch (e) {
      console.warn('[bdef-search] destino', e);
    }
    saveRecent(v);
    this.track('search_submit', { search_term: v, search_results_count: res.total, destination: res.url, fix: res.fix, source });
    window.location.href = res.url;
  }

  // ── Destino de "Ver todos" ────────────────────────────────────────────────

  /**
   * Destino de "Ver todos" e Intro. Nunca manda a la búsqueda nativa sin acotar:
   * colección (o Todos los productos) con los filtros de Search & Discovery ya aplicados,
   * y /search solo para las palabras que no tienen filtro, también con filtros.
   */
  viewAllFor(p, chipTerm, facets = [], exact = true, refine = {}) {
    const refinedGender = refine.gender ? norm(refine.gender.label) : null;
    const g = p.genero?.h || (['mujer', 'hombre', 'kids'].includes(refinedGender) ? refinedGender : undefined);
    const G = CFG.GENDER_LABEL;
    const F = CFG.FACET;
    const lc = s => String(s).toLowerCase();
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const searchUrl = q => `${root}/search?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last`;

    // Sin resultados exactos (erratas, referencias): búsqueda natural como hasta ahora
    if (!exact) return [{ url: searchUrl(String(p.raw || p.text).trim()), label: 'Ver todos los resultados' }];

    // Valor de URL de cada filtro, tal y como lo devuelve Shopify
    const inputValue = input => {
      try {
        const o = JSON.parse(input);
        return o.productType ?? o.productVendor ?? o.productMetafield?.value ?? o.variantOption?.value ?? null;
      } catch (e) { return null; }
    };
    const facetValues = (id, labels) => {
      const want = new Set(labels.map(norm));
      const facet = facets.find(f => f.id === id);
      return facet ? facet.values.filter(v => want.has(norm(v.label))).map(v => inputValue(v.input)).filter(Boolean) : [];
    };

    // Lo que no se puede expresar con filtros va como palabra de búsqueda
    const free = [];
    const typeTerm = chipTerm && chipTerm.types.length && (chipTerm.mode === 'tipo' || chipTerm.mode === 'tipo_y_titulo') ? chipTerm : null;
    if (chipTerm && !typeTerm) free.push(chipTerm.titles[0] || chipTerm.label);
    if (typeTerm && typeTerm.mode === 'tipo_y_titulo' && typeTerm.titles.length) free.push(typeTerm.titles[0]);
    p.estilo.forEach(t => free.push(t.titles[0] || t.label));
    p.rest.forEach(w => free.push(w));

    const colorVals = [];
    p.color.forEach(t => {
      const v = facetValues(F.color, [t.label, ...(t.aliases || [])]);
      if (v.length) colorVals.push(...v);
      else free.push(t.titles[0] || t.label);
    });
    const genderVals = p.genero ? facetValues(F.gender, p.genero.tags.length ? p.genero.tags : [p.genero.label]) : [];
    const vendorVals = p.marca.flatMap(t => t.vendors);
    // Refinado de la fila guiada: mismos filtros de Search & Discovery en el destino
    const refVal = d => (refine[d] ? inputValue(refine[d].input) : null);
    if (refVal('gender')) genderVals.push(refVal('gender'));
    if (refVal('color')) colorVals.push(refVal('color'));
    if (refVal('vendor')) vendorVals.push(refVal('vendor'));
    const sizeVal = refVal('size');

    const withFilters = (base, { type = false, gender = true } = {}) => {
      const u = new URLSearchParams();
      if (type && typeTerm) typeTerm.types.forEach(v => u.append(F.type, v));
      if (gender) genderVals.forEach(v => u.append(F.gender, v));
      vendorVals.forEach(v => u.append(F.vendor, v));
      colorVals.forEach(v => u.append(F.color, v));
      if (sizeVal) { u.append(F.size, sizeVal); u.append(F.avail, '1'); }
      const qs = u.toString();
      return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
    };

    // 1. Novedades: colección de novedades del género, filtrada por tipo, marca y color
    if (p.intencion && !p.intencion.tags.length) {
      const c = p.intencion.cols;
      if (g && c[g]) return [{ url: withFilters(c[g], { type: true, gender: false }), label: `Ver ${lc(p.intencion.label)} ${G[g]}` }];
      const split = ['mujer', 'hombre', 'kids'].filter(k => c[k]);
      if (split.length) {
        const list = split.map(k => ({ url: withFilters(c[k], { type: true, gender: false }), label: `${p.intencion.label} ${G[k]}` }));
        // Intro: todas las novedades, de la más reciente a la más antigua
        list.enter = withFilters(`${CFG.ALL_URL}?sort_by=created-descending`, { type: true });
        return list;
      }
    }

    // 2. Palabras sin filtro posible: /search con esas palabras y los filtros que sí existen
    if (free.length) return [{ url: withFilters(searchUrl(free.join(' ')), { type: true }), label: 'Ver todos los resultados' }];

    // 3. Tipo de prenda: colección del género o Todos los productos filtrado por tipo
    if (typeTerm) {
      if (g && typeTerm.cols[g]) return [{ url: withFilters(typeTerm.cols[g], { gender: false }), label: `Ver ${lc(typeTerm.label)} ${G[g]}` }];
      if (typeTerm.cols.general) return [{ url: withFilters(typeTerm.cols.general), label: `Ver ${lc(typeTerm.label)}` }];
      return [{ url: withFilters(CFG.ALL_URL, { type: true }), label: `Ver ${lc(typeTerm.label)}` }];
    }

    // 4. Marca: su colección, filtrada por género y color
    const marca = p.marca[0];
    if (marca) return [{ url: withFilters(marca.cols?.general || CFG.ALL_URL), label: `Ver todo ${marca.label}` }];

    // 5. Solo género (y color): colección del género
    if (p.genero?.cols?.general) return [{ url: withFilters(p.genero.cols.general, { gender: false }), label: `Ver moda ${G[g] || ''}`.trim() }];

    // 6. Solo color
    if (colorVals.length) return [{ url: withFilters(CFG.ALL_URL), label: 'Ver todos los resultados' }];

    return [{ url: searchUrl(p.broadTokens.join(' ') || p.text), label: 'Ver todos los resultados' }];
  }

  // ── Render ────────────────────────────────────────────────────────────────

  render(term, p, r) {
    injectStyles();
    const activeChip = r.chips[0] || null;
    const hasExact = r.exactNodes.length > 0;
    const nodes = hasExact ? r.exactNodes : r.broadNodes;
    const total = hasExact ? r.exactTotal : r.broadNodes.length;
    const viewAll = this.viewAllFor(p, activeChip?.term || null, r.facets || [], !(r.plan.exact && !hasExact), r.refine);

    this.state = { term, p, r, active: 0, viewAll, total };
    this._needSizes = false;
    // Con la última palabra a medio escribir ("camiseta ma") manda el autocompletado, no el refinado
    const words = p.text.split(' ');
    const lastWord = words[words.length - 1];
    const typing = p.rest.includes(lastWord) && !!this._map && [...this._map.index.keys()].some(a => a !== lastWord && a.startsWith(lastWord));
    const refineRow = !typing && !r.plan.novelty && r.refine && (hasExact || Object.keys(r.refine).length) ? this.refineHtml(p, r) : '';

    let html = '';
    if (p.shortcut) html += this.bannerHtml(p.shortcut.url, p.shortcut.label, 'Ir ahora', 'shortcut');

    // "¿Quisiste decir…?" cuando la búsqueda exacta no encuentra nada por una palabra desconocida
    const dym = !hasExact && this._map ? didYouMean(p, this._map) : [];
    if (dym.length) {
      html += `<p class="bdef-ps__dym">¿Quisiste decir ${dym.map(d => `<button type="button" data-suggest="${esc(d.text)}" data-suggest-type="dym">${esc(d.text)}</button>`).join(' o ')}?</p>`;
    }
    // Mientras se escribe, sugerencias de texto (con el stock de la búsqueda sin la palabra a medias);
    // cuando la búsqueda ya se entiende, la fila de refinado. Nunca las dos a la vez.
    if (refineRow) {
      html += refineRow;
    } else {
      const base = typing && words.length > 1 ? this.cache.get(`${words.slice(0, -1).join(' ')}#${refineKey({})}`) : null;
      const sugg = this._map ? typeahead(p, this._map, r.queries || [], base?.facets || null).filter(s => !dym.some(d => norm(d.text) === norm(s.text))) : [];
      if (sugg.length) {
        html += '<div class="bdef-ps__sugg" role="group" aria-label="Sugerencias de búsqueda">' +
          sugg.map(s => `<button type="button" data-suggest="${esc(s.text)}" data-suggest-type="typeahead">${CHEVRON_SEARCH}${esc(s.text)}${s.count ? `<span>${s.count}</span>` : ''}</button>`).join('') + '</div>';
      }
    }

    const genderOnly = !nodes.length && p.genero && !p.tipo.length && !p.marca.length && !p.rest.length;

    if (nodes.length) {
      if (p.corrections.length && hasExact) html += `<p class="bdef-ps__section">Resultados para «${esc(p.corrections.map(c => c.label).join(', '))}»</p>`;
      if (r.approx) html += '<p class="bdef-ps__section">Resultados aproximados</p>';
      if (r.chips.length > 1 && hasExact && !Object.keys(r.refine || {}).length) html += this.chipsHtml(r.chips, 0);
      html += `<div class="bdef-ps__grid" data-grid>${this.cardsHtml(nodes)}</div>`;
      if (!hasExact && r.exactTotal === 0 && r.plan.exact) html = html.replace('<div class="bdef-ps__grid"', '<p class="bdef-ps__section">Resultados aproximados</p><div class="bdef-ps__grid"');
      html += this.viewAllHtml(viewAll);
      if (r.collections.length) {
        html += '<p class="bdef-ps__section">Colecciones</p><div class="bdef-ps__cols">' +
          r.collections.map(c => `<a href="/collections/${esc(c.handle)}${c.trackingParameters ? '?' + esc(c.trackingParameters) : ''}" data-collection="${esc(c.handle)}">${esc(c.title)}</a>`).join('') + '</div>';
      }
    } else if (genderOnly) {
      html += viewAll.map(v => this.bannerHtml(v.url, v.label, 'Ver colección', 'gender')).join('');
      html += this.trendingHtml('Lo más buscado');
    } else if (!p.shortcut) {
      html += `<p class="bdef-ps__empty">No hay resultados para «${esc(term)}».<br>Prueba con otra palabra o con una de estas búsquedas.</p>`;
      html += this.trendingHtml('');
    } else {
      html += this.trendingHtml('');
    }

    this.slotEl.innerHTML = `<div class="bdef-ps${refineRow ? ' bdef-ps--refining' : ''}" role="region" aria-label="Resultados de búsqueda">${html}</div>`;
    this.announce(nodes.length ? `${total} productos` : 'Sin resultados');
    if (this._needSizes) this.loadSizes(r);
    this.scheduleTrack(term, p, total, hasExact ? 'exact' : (nodes.length ? 'broad' : (p.shortcut ? 'shortcut' : 'none')));
  }

  // ── Búsqueda guiada ───────────────────────────────────────────────────────

  /** Opciones con stock de cada dimensión para esta búsqueda; null si no aplica o ya está decidida. */
  refineOptions(p, r) {
    const F = CFG.FACET, R = r.refine || {};
    const values = id => (r.facets.find(f => f.id === id)?.values || []).filter(v => v.count > 0);
    const byCount = list => [...list].sort((a, b) => b.count - a.count);
    const genders = values(F.gender).filter(v => /^(mujer|hombre|kids)$/i.test(norm(v.label)));
    const genderOpen = !p.genero && !R.gender && genders.length > 1;
    const out = {};
    out.gender = genderOpen ? genders : null;
    // Talla: solo con una prenda concreta y el género ya claro (ropa y calzado usan escalas distintas)
    if (!R.size && p.tipo.length && !genderOpen && values(F.size).length) {
      out.size = r.sizeCounts ? (r.sizeCounts.length > 1 ? r.sizeCounts : null) : 'pending';
    } else out.size = null;
    const colors = byCount(values(F.color));
    out.color = !p.color.length && !R.color && colors.length > 1 ? colors : null;
    const vendors = byCount(values(F.vendor));
    out.vendor = !p.marca.length && !R.vendor && vendors.length > 1 ? vendors : null;
    return out;
  }

  refineHtml(p, r) {
    const R = r.refine || {};
    const opts = this.refineOptions(p, r);
    const open = CFG.REFINE_ORDER.filter(d => opts[d]);
    const pending = open.filter(d => !this.skipped.has(d));
    const dim = pending[0] || null;
    this._needSizes = dim === 'size' && opts.size === 'pending';
    const chosen = CFG.REFINE_ORDER.filter(d => R[d]).map(d =>
      `<button type="button" class="bdef-ps__chip" aria-pressed="true" data-unrefine="${d}" aria-label="Quitar ${esc(R[d].label)}">${esc(R[d].label)}<span aria-hidden="true">×</span></button>`).join('');
    let options = '';
    if (dim && opts[dim] !== 'pending') {
      const list = opts[dim].slice(0, CFG.REFINE_MAX[dim]);
      options = `<span class="bdef-ps__refine-label">${CFG.REFINE_LABEL[dim]}</span>` + list.map(v =>
        `<button type="button" class="bdef-ps__chip" aria-pressed="false" data-refine="${dim}" data-label="${esc(v.label)}" data-input="${esc(v.input)}">${esc(v.label)}<span>${v.count}</span></button>`).join('');
    } else if (dim) {
      options = `<span class="bdef-ps__refine-label">${CFG.REFINE_LABEL[dim]}</span>`;
    }
    const more = open.length > 1 || (dim && this.skipped.size) ? `<button type="button" class="bdef-ps__refine-more" data-refine-next="${dim || ''}">Más filtros</button>` : '';
    if (!chosen && !options) return '';
    return `<div class="bdef-ps__refine" role="group" aria-label="Refinar resultados" data-refine-row>${chosen}${options}${more}</div>`;
  }

  /** Recuento real por talla: talla + disponible juntas filtran por variante con stock. */
  async loadSizes(r) {
    if (r.sizeCounts || r._sizesLoading || !r.plan.exact) return;
    r._sizesLoading = true;
    const values = (r.facets.find(f => f.id === CFG.FACET.size)?.values || []).filter(v => v.count > 0)
      .sort((a, b) => sizeRank(a.label) - sizeRank(b.label)).slice(0, CFG.REFINE_MAX.size);
    const defs = ['$q: String!'], body = [], vars = { q: r.plan.exact };
    values.forEach((v, i) => {
      defs.push(`$f${i}: [ProductFilter!]`);
      vars['f' + i] = [...r.filters.filter(f => !('available' in f)), JSON.parse(v.input), { available: true }];
      body.push(`s${i}: search(query: $q, first: 1, types: PRODUCT, unavailableProducts: HIDE, productFilters: $f${i}) { totalCount }`);
    });
    try {
      const data = values.length ? await gql({ query: `query BdefSizes(${defs.join(', ')}) { ${body.join(' ')} }`, variables: vars }) : {};
      r.sizeCounts = values.map((v, i) => ({ label: v.label, input: v.input, count: data['s' + i]?.totalCount || 0 })).filter(v => v.count > 0);
    } catch (e) {
      console.warn('[bdef-search] tallas', e);
      r.sizeCounts = [];
    }
    r._sizesLoading = false;
    if (this.state?.r === r) this.updateRefineRow();
  }

  updateRefineRow() {
    const s = this.state;
    if (!s?.r) return;
    const row = this.slotEl.querySelector('[data-refine-row]');
    const html = this.refineHtml(s.p, s.r);
    if (row) row.outerHTML = html || '';
    if (this._needSizes) this.loadSizes(s.r);
  }

  isMobile() {
    return window.matchMedia?.('(max-width: 749px)').matches;
  }

  /** Caja vacía: búsquedas recientes del cliente y lo más buscado. */
  async renderZero() {
    if (!this._map) {
      try { const map = await loadMap(); this._map = map; this._trending = map.trending; this._vendorUrls = map.vendorUrls; } catch (e) { /* sin mapa */ }
    }
    if (this.input.value.trim()) return;
    injectStyles();
    const recent = readRecent();
    let html = '';
    if (recent.length) {
      html += '<p class="bdef-ps__section bdef-ps__section--row">Tus búsquedas recientes<button type="button" data-recent-clear>Borrar</button></p>' +
        '<div class="bdef-ps__sugg" role="group" aria-label="Búsquedas recientes">' +
        recent.map(t => `<button type="button" data-suggest="${esc(t)}" data-suggest-type="recent">${CHEVRON_SEARCH}${esc(t)}</button>`).join('') + '</div>';
    }
    html += this.trendingHtml('Lo más buscado');
    if (!html) { this.clear(); return; }
    this.state = { term: '', zero: true };
    this.slotEl.innerHTML = `<div class="bdef-ps bdef-ps--zero" role="region" aria-label="Sugerencias de búsqueda">${html}</div>`;
  }

  renderError(term) {
    injectStyles();
    this.slotEl.innerHTML = `<div class="bdef-ps"><p class="bdef-ps__empty">La búsqueda no ha respondido. Pulsa Intro para ver los resultados de «${esc(term)}».</p></div>`;
  }

  chipsHtml(chips, active) {
    return '<div class="bdef-ps__chips" role="group" aria-label="Filtrar por tipo">' +
      chips.map((c, i) => `<button type="button" class="bdef-ps__chip" data-chip="${i}" aria-pressed="${i === active}">${esc(c.term.label)}<span>${c.count}</span></button>`).join('') +
      '</div>';
  }

  // ── Tarjeta de producto: mismo marcado y clases que snippets/product-card.liquid ──

  formatMoney(amount, withCurrency) {
    const C = CFG.CARD;
    const fmt = withCurrency ? C.moneyWithCurrencyFormat : C.moneyFormat;
    const cents = Math.round((parseFloat(amount) || 0) * 100);
    const fix = (n, d, ts, ds) => {
      const parts = (n / 100).toFixed(d).split('.');
      return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ts) + (parts[1] ? ds + parts[1] : '');
    };
    return String(fmt || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      switch (k) {
        case 'amount': return fix(cents, 2, ',', '.');
        case 'amount_no_decimals': return fix(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return fix(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fix(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fix(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fix(cents, 2, ' ', ',');
        default: return fix(cents, 2, '.', ',');
      }
    }).replace(/<[^>]*>/g, '');
  }

  imageTag(img, cls, loading, hidden = false) {
    if (!img) return '';
    const join = img.url.includes('?') ? '&' : '?';
    const widths = [200, 300, 400, 500, 600, 800].filter(w => !img.width || w <= img.width);
    const srcset = widths.map(w => `${img.url}${join}width=${w} ${w}w`).join(', ');
    const sizes = '(max-width: 699px) 50vw, (max-width: 999px) 33vw, 20vw';
    return `<img src="${esc(img.url + join + 'width=400')}" srcset="${esc(srcset)}" sizes="${sizes}" alt="${esc(img.altText || '')}"${img.width ? ` width="${img.width}" height="${img.height}"` : ''} loading="${loading}"${hidden ? ' fetchpriority="low" aria-hidden="true"' : ''} draggable="false" class="${cls}">`;
  }

  cardsHtml(nodes) {
    const C = CFG.CARD, T = C.t || {};
    const now = Date.now();
    const textClass = C.headingFont === 'heading' ? 'h6' : '';
    const aspect = String(C.aspect || 'natural');
    const aspectClass = `${aspect.includes('crop') ? 'object-cover ' : ''}aspect-${aspect.split('_')[0]}`;
    const lines = Number(C.titleLines) || 0;

    return nodes.slice(0, CFG.EXACT_LIMIT).map((n, idx) => {
      const url = `/products/${n.handle}${n.trackingParameters ? '?' + n.trackingParameters : ''}`;
      const min = parseFloat(n.priceRange.minVariantPrice.amount) || 0;
      const max = parseFloat(n.priceRange.maxVariantPrice?.amount) || min;
      const cmpMin = parseFloat(n.compareAtPriceRange?.minVariantPrice?.amount) || 0;
      const cmpMax = parseFloat(n.compareAtPriceRange?.maxVariantPrice?.amount) || cmpMin;
      const onSale = cmpMin > min;
      const price = this.formatMoney(min, C.currencyCode);

      // Insignias, mismo orden que product-badges.liquid: novedad, personalizadas, descuento
      let badges = '';
      if (n.createdAt && (now - new Date(n.createdAt)) / 86400000 < CFG.NEW_DAYS) badges += `<span class="badge badge--new">${esc(T.newBadge || 'Novedad')}</span>`;
      try {
        (JSON.parse(n.badges?.value || '[]') || []).forEach(b => {
          const h = norm(b).replace(/\s+/g, '-');
          badges += `<span class="badge badge--custom badge--${esc(h)}">${esc(b)}</span>`;
        });
      } catch (e) { /* metafield no expuesto */ }
      if (C.showDiscount !== false && onSale) {
        let text;
        if (cmpMin !== cmpMax || !cmpMin) text = T.onSale || 'En oferta';
        else {
          const savings = C.discountMode === 'percentage' ? `${Math.round((cmpMin - min) / cmpMin * 100)}%` : this.formatMoney(cmpMin - min, C.currencyCode);
          text = (T.discount || '-__S__').replace('__S__', savings);
        }
        badges += `<on-sale-badge class="badge badge--on-sale">${text.replace(/<[^>]*>/g, '')}</on-sale-badge>`;
      }
      const badgeList = badges ? `<badge-list class="badge-list badge-list--vertical">${badges}</badge-list>` : '';

      // Imágenes: principal y secundaria al pasar el ratón, como en la colección
      const imgs = n.images?.nodes || [];
      const mainImg = n.cardImage?.reference?.image || n.featuredImage || imgs[0];
      const hoverImg = n.cardHover?.reference?.image || imgs.find(i => i.url !== n.featuredImage?.url) || imgs[1];
      const primary = this.imageTag(mainImg, `product-card__image product-card__image--primary product-card__image--border ${aspectClass}`, idx < 3 ? 'eager' : 'lazy');
      const second = C.showSecondary && imgs.length > 1 && hoverImg ? this.imageTag(hoverImg, 'product-card__image product-card__image--border product-card__image--secondary', 'lazy', true) : '';

      // Precio con la estrategia "desde" del tema
      const saleClass = `${textClass} ${onSale ? 'text-on-sale' : 'text-subdued'}`.trim();
      const shown = max > min && C.priceStrategy === 'from_price' ? (T.fromPrice || 'Desde __P__').replace('__P__', price) : price;
      let priceList = `<sale-price class="${saleClass}"><span class="sr-only">${esc(T.salePrice || 'Precio de venta')}</span>${shown.replace(/<(?!\/?span)[^>]*>/g, '')}</sale-price>`;
      if (onSale) priceList += `<compare-at-price class="${`${textClass} text-subdued line-through`.trim()}"><span class="sr-only">${esc(T.regularPrice || 'Precio habitual')}</span>${this.formatMoney(cmpMin, C.currencyCode)}</compare-at-price>`;

      const vendorUrl = this._vendorUrls?.get(n.vendor) || `/collections/vendors?q=${encodeURIComponent(n.vendor || '')}`;
      const vendor = C.showVendor && n.vendor ? `<a href="${esc(vendorUrl)}" class="vendor smallcaps ${C.headingFont === 'heading' ? 'heading' : ''}">${esc(n.vendor)}</a>` : '';
      const titleAttr = lines > 0 ? ` style="--line-clamp-count: ${lines}"` : '';

      return `<product-card class="product-card" handle="${esc(n.handle)}" data-pos="${idx + 1}">
<div class="product-card__figure">${badgeList}<a href="${esc(url)}" class="product-card__media" draggable="false" data-instant>${primary}${second}</a></div>
<div class="product-card__info empty:hidden"><div class="v-stack justify-items-center gap-2">${vendor}<div class="v-stack justify-items-center gap-1">${C.titleWrap ? '<p>' : ''}<a href="${esc(url)}" class="product-title ${textClass} ${lines > 0 ? 'line-clamp' : ''}"${titleAttr} data-instant>${esc(n.title)}</a>${C.titleWrap ? '</p>' : ''}<price-list class="price-list">${priceList}</price-list></div></div></div>
</product-card>`;
    }).join('');
  }

  viewAllHtml(list) {
    return '<div class="bdef-ps__more">' + list.map(v => `<a href="${esc(v.url)}" data-viewall>${esc(v.label)}</a>`).join('') + '</div>';
  }

  bannerHtml(url, label, sub, kind) {
    return `<a href="${esc(url)}" class="bdef-ps__banner" data-banner="${kind}"><span>${esc(label)}<small>${esc(sub)}</small></span>${CHEVRON}</a>`;
  }

  trendingHtml(title) {
    const list = this.mapTrending();
    if (!list.length) return '';
    return (title ? `<p class="bdef-ps__section" style="text-align:center">${esc(title)}</p>` : '') +
      '<div class="bdef-ps__trend">' + list.map(t => `<a href="${esc(t.u)}" data-trend>${esc(t.l)}</a>`).join('') + '</div>';
  }

  mapTrending() {
    return this._trending || [];
  }

  announce(text) {
    const el = document.getElementById('status-announcement');
    if (el) el.textContent = text;
  }

  // ── Interacción ───────────────────────────────────────────────────────────

  onClick(e) {
    const s = this.state;
    if (!s) return;

    const chip = e.target.closest('[data-chip]');
    if (chip) { this.selectChip(Number(chip.dataset.chip)); return; }

    const ref = e.target.closest('[data-refine]');
    if (ref) {
      const dim = ref.dataset.refine;
      this.refine = { ...this.refine, [dim]: { label: ref.dataset.label, input: ref.dataset.input } };
      this.skipped = new Set();
      this.track('search_refine', { search_term: s.term, dim, value: ref.dataset.label });
      if (this.isMobile()) this.input.blur();   // el cliente ya elige: el teclado deja sitio a la rejilla
      this.onInput();
      return;
    }
    const unref = e.target.closest('[data-unrefine]');
    if (unref) {
      const next = { ...this.refine };
      delete next[unref.dataset.unrefine];
      this.refine = next;
      this.skipped = new Set();
      this.onInput();
      return;
    }
    const more = e.target.closest('[data-refine-next]');
    if (more) {
      const dim = more.dataset.refineNext;
      if (dim) this.skipped.add(dim);
      // Vuelta completa: se empieza otra vez por la primera dimensión abierta
      const opts = this.refineOptions(s.p, s.r);
      if (CFG.REFINE_ORDER.filter(d => opts[d]).every(d => this.skipped.has(d))) this.skipped = new Set();
      this.updateRefineRow();
      return;
    }
    if (e.target.closest('[data-recent-clear]')) {
      try { localStorage.removeItem(CFG.RECENT_KEY); } catch (err) { /* sin almacenamiento */ }
      this.renderZero();
      return;
    }

    const sug = e.target.closest('[data-suggest]');
    if (sug) {
      const text = sug.dataset.suggest;
      this.track('search_suggestion_click', { search_term: s.term, suggestion: text, suggestion_type: sug.dataset.suggestType });
      this.input.value = text;
      this.input.focus();
      this.onInput();
      return;
    }

    const card = e.target.closest('product-card[data-pos]');
    if (card && e.target.closest('a.product-card__media, a.product-title')) {
      const nodes = this.currentNodes();
      const n = nodes.find(x => x.handle === card.getAttribute('handle'));
      if (n) {
        saveRecent(s.term);
        this.track('select_item', {
          item_list_id: 'predictive_search',
          item_list_name: `búsqueda: ${s.term}`,
          currency: n.priceRange.minVariantPrice.currencyCode || CFG.CURRENCY,
          items: [{
            item_id: n.handle,
            item_name: n.title,
            item_brand: n.vendor,
            item_category: n.productType,
            index: Number(card.dataset.pos),
            price: parseFloat(n.priceRange.minVariantPrice.amount) || 0
          }]
        });
      }
      return;
    }

    const viewAllLink = e.target.closest('[data-viewall]');
    if (viewAllLink) {
      this.track('view_search_results', { search_term: s.term, results_shown: s.total, destination: viewAllLink.getAttribute('href') });
      return;
    }

    const banner = e.target.closest('[data-banner]');
    if (banner) {
      this.track('search_smart_link', { search_query: s.term, link_label: banner.textContent.trim(), link_url: banner.getAttribute('href') });
      return;
    }

    const col = e.target.closest('[data-collection]');
    if (col) this.track('search_collection_click', { search_query: s.term, collection: col.dataset.collection });
  }

  currentNodes() {
    const s = this.state;
    const chip = s.r.chips[s.active];
    return (chip && chip.nodes && chip.nodes.length) ? chip.nodes : (s.r.exactNodes.length ? s.r.exactNodes : s.r.broadNodes);
  }

  async selectChip(i) {
    const s = this.state;
    const chip = s.r.chips[i];
    if (!chip || i === s.active) return;
    const grid = this.slotEl.querySelector('[data-grid]');
    this.slotEl.querySelectorAll('[data-chip]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.chip) === i)));
    s.active = i;
    this.track('search_chip', { search_term: s.term, chip: chip.term.label });

    if (!chip.nodes) {
      grid?.setAttribute('aria-busy', 'true');
      try {
        const data = await gql({
          query: `query BdefChip($q: String!) { search(query: $q, first: ${CFG.EXACT_LIMIT}, types: PRODUCT, unavailableProducts: HIDE) { totalCount nodes { ... on Product { ${PRODUCT_FIELDS} } } } }`,
          variables: { q: chip.query }
        });
        chip.nodes = (data.search?.nodes || []).filter(n => n.featuredImage);
      } catch (e) {
        console.error('[bdef-search] chip', e);
        chip.nodes = [];
      }
      grid?.removeAttribute('aria-busy');
      if (this.state !== s || s.active !== i) return;
    }

    if (grid) grid.innerHTML = this.cardsHtml(chip.nodes);
    s.viewAll = this.viewAllFor(s.p, chip.term, s.r.facets || [], true, s.r.refine);
    s.total = chip.count;
    const more = this.slotEl.querySelector('.bdef-ps__more');
    if (more) more.outerHTML = this.viewAllHtml(s.viewAll);
    this.announce(`${chip.count} ${chip.term.label.toLowerCase()}`);
  }

  // ── Medición (Clarity) ───────────────────────────────────────────────────

  /**
   * BDEF-CUSTOM: medición en Microsoft Clarity (cargado en la página por su integración de app).
   * - clarity('event'): eventos personalizados, filtrables en grabaciones y paneles.
   * - clarity('set'): etiquetas de sesión (término, nº de resultados, género, chip, posición del clic…).
   * - clarity('upgrade'): marca la sesión como prioritaria para que se conserve su grabación.
   * GA4 no está cargado en la página: las búsquedas enviadas (Intro → /search) llegan a GA4
   * por Simprosys como evento estándar de Shopify.
   */
  track(name, params) {
    const c = window.clarity;
    if (typeof c !== 'function') return;
    const p = params || {};
    const ev = CLARITY_EVENTS[name] || name;
    const tag = (k, v) => { if (v !== undefined && v !== null && v !== '') c('set', k, String(v).slice(0, 100)); };
    const term = safeTerm(p.search_term ?? p.search_query);

    try {
      c('event', ev);
      tag('search_term', term);
      switch (name) {
        case 'search':
          tag('search_results', bucket(p.search_results_count));
          tag('search_gender', p.search_gender_filter);
          tag('search_type', p.search_type);
          break;
        case 'search_no_results':
          tag('search_no_results', 'true');
          tag('search_gender', p.search_gender_filter);
          c('upgrade', 'search_no_results');
          break;
        case 'search_chip':
          tag('search_chip', p.chip);
          break;
        case 'select_item': {
          const it = p.items?.[0] || {};
          tag('search_click_position', it.index);
          tag('search_click_type', it.item_category);
          tag('search_click_brand', it.item_brand);
          break;
        }
        case 'view_search_results':
          tag('search_destination', p.destination);
          break;
        case 'search_smart_link':
          tag('search_smart_link', p.link_label);
          break;
        case 'search_collection_click':
          tag('search_collection', p.collection);
          break;
        case 'search_submit':
          tag('search_results', bucket(p.search_results_count));
          tag('search_destination', p.destination);
          tag('search_fix', p.fix);
          tag('search_source', p.source);
          c('upgrade', 'search_submit');
          break;
        case 'search_suggestion_click':
          tag('search_suggestion', p.suggestion);
          tag('search_suggestion_type', p.suggestion_type);
          break;
        case 'search_fix_undo':
          tag('search_fix_undo', p.fix);
          break;
        case 'search_refine':
          tag('search_refine', `${p.dim}:${p.value}`);
          break;
      }
    } catch (e) {
      console.warn('[bdef-search] clarity', e);
    }
  }

  /** Un evento search por término cuando el usuario deja de escribir, no uno por pulsación. */
  scheduleTrack(term, p, count, type) {
    clearTimeout(this.trackTimer);
    this.trackTimer = setTimeout(() => {
      if (term === this.lastTracked) return;
      this.lastTracked = term;
      const gender = p.genero?.label || 'ninguno';
      this.track('search', { search_term: term, search_results_count: count, search_gender_filter: gender, search_type: type });
      if (!count) this.track('search_no_results', { search_query: term, search_gender_filter: gender });
    }, CFG.TRACK_IDLE_MS);
  }
}

if (!window.customElements.get('predictive-search')) {
  window.customElements.define('predictive-search', PredictiveSearch);
}

// ─── PÁGINAS: formularios de /search, aviso de corrección y "¿Quisiste decir?" ──

const PAGE_HELPER = Object.assign(Object.create(PredictiveSearch.prototype), { cache: new Map() });

function pageNotice(html) {
  const box = document.createElement('div');
  box.className = 'bdef-search-notice';
  box.setAttribute('role', 'status');
  box.innerHTML = `<div class="container">${html}</div>`;
  const style = document.createElement('style');
  style.textContent = '.bdef-search-notice{padding:14px 0;font-size:13px;line-height:1.5;text-align:center;border-bottom:1px solid #eee}.bdef-search-notice a{font-weight:600;text-decoration:underline;text-underline-offset:3px}';
  document.head.appendChild(style);
  const main = document.getElementById('main') || document.body;
  main.prepend(box);
  return box;
}

function initSearchPages() {
  const params = new URLSearchParams(location.search);
  const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');

  // 1. Cuadros de búsqueda de la página (/search, bloque Descubrir, 404)
  //    - Al pulsar o escribir en ellos se abre el buscador de la cabecera con el texto: resultados
  //      y sugerencias mientras se escribe, igual que arriba.
  //    - Si se envían (botón Buscar o Intro antes de pasar a la cabecera), van directos al destino
  //      con filtros. Se intercepta en captura para que ningún otro script abra además la cabecera.
  // Se comprueba al usarlo, no al cargar: <header-search> lo define theme.js, que puede cargarse después.
  const headerParts = () => {
    const hs = document.querySelector('header-search');
    const hi = hs?.querySelector('input[name="q"]');
    return hi && typeof hs.show === 'function' ? [hs, hi] : null;
  };
  const openHeader = text => {
    const parts = headerParts();
    if (!parts) return false;
    const [headerSearch, headerInput] = parts;
    const fill = () => {
      headerInput.value = text;
      headerInput.focus();
      try { headerInput.setSelectionRange(text.length, text.length); } catch (e) { /* type=search */ }
      if (text.trim()) headerInput.dispatchEvent(new Event('input', { bubbles: true }));
    };
    let shown;
    try { shown = headerSearch.show(); } catch (e) { return false; }
    fill();   // en el mismo gesto, para que el teclado del móvil se abra en la cabecera
    Promise.resolve(shown).catch(() => {}).then(() => { if (document.activeElement !== headerInput) headerInput.focus(); });
    return true;
  };

  const pageForms = [...document.querySelectorAll('form[action$="/search"]')].filter(f => f.id !== 'predictive-search-form' && !f.dataset.bdefWired);
  pageForms.forEach(f => {
    f.dataset.bdefWired = '1';
    const input = f.elements.q;
    if (!input) return;
    input.addEventListener('pointerdown', e => {
      if (e.button || !headerParts()) return;
      e.preventDefault();
      openHeader(input.value);
    });
    input.addEventListener('input', () => { openHeader(input.value); });
  });
  if (pageForms.length) {
    document.addEventListener('submit', e => {
      const f = e.target;
      if (!pageForms.includes(f)) return;
      const q = (f.elements.q?.value || '').trim();
      if (!q) return;
      e.preventDefault();
      e.stopPropagation();
      PAGE_HELPER.go(q, 'page');
    }, true);
  }

  // 2. Aviso tras una corrección automática al pulsar Intro ("Mostrando resultados para…")
  const shownAs = params.get('bdef_as'), original = params.get('bdef_q');
  if (shownAs && original) {
    const undo = `${root}/search?q=${encodeURIComponent(original)}&type=product`;
    const box = pageNotice(`Mostrando resultados para <strong>${esc(shownAs)}</strong>. ¿Buscabas <a href="${esc(undo)}" data-bdef-undo>${esc(original)}</a>?`);
    box.querySelector('[data-bdef-undo]')?.addEventListener('click', () => PAGE_HELPER.track('search_fix_undo', { search_term: original, fix: shownAs }));
    params.delete('bdef_as'); params.delete('bdef_q');
    const qs = params.toString();
    history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
    return;
  }

  // 3. Página /search: "¿Quisiste decir…?" con el texto corregido
  const q = params.get('q');
  if (!q || !/\/search\/?$/.test(location.pathname)) return;
  loadMap().then(map => {
    if (!map.ready) return;
    const p = parse(q, map);
    const options = [];
    const auto = correctedText(p, map);
    if (auto) options.push(auto);
    didYouMean(p, map).forEach(d => options.push(d.text));
    const uniq = [...new Set(options.map(o => o.trim()))].filter(o => norm(o) !== p.text).slice(0, CFG.DYM_LIMIT);
    if (!uniq.length) return;
    const box = pageNotice('¿Quisiste decir ' + uniq.map(o => `<a href="${esc(`${root}/search?q=${encodeURIComponent(o)}&type=product`)}" data-bdef-dym="${esc(o)}">${esc(o)}</a>`).join(' o ') + '?');
    box.querySelectorAll('[data-bdef-dym]').forEach(a => a.addEventListener('click', e => {
      e.preventDefault();
      PAGE_HELPER.track('search_suggestion_click', { search_term: q, suggestion: a.dataset.bdefDym, suggestion_type: 'dym_page' });
      PAGE_HELPER.go(a.dataset.bdefDym, 'dym_page');
    }));
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSearchPages);
else initSearchPages();